import { ChevronLeft, ChevronRight, CornerDownRight } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

import { ChapterKeyPoints, ChapterPurposePassages } from './chapter-highlights'
import { PurposeQuestions } from './purpose-questions'
import { QuestionItem } from './question-item'
import type { BookAiReaderState } from './useBookAi'

type Props = {
  bookAi: BookAiReaderState
  onNavigate: (key: number) => void
}

/**
 * いまの章: 要約・目的に効く箇所・読む前の問い・重要箇所・キーワード（既定は今読んでいる章。前後の章もめくれる）
 */
export const SectionTab = ({ bookAi, onNavigate }: Props) => {
  const { sections, currentSectionIndex } = bookAi
  // null のときは今読んでいる章に追従する
  const [browsingIndex, setBrowsingIndex] = useState<number | null>(null)
  // 開いている「答えてカードにする」（章の番号-問いの番号）
  const [openQuestion, setOpenQuestion] = useState<string | null>(null)
  const index = browsingIndex ?? Math.max(currentSectionIndex, 0)
  const section = sections.find((s) => s.index === index)

  if (!section) {
    return <p className="py-8 text-center text-sm text-muted-foreground">章の情報がまだありません</p>
  }

  const isCurrent = index === currentSectionIndex
  const analysis = section.analysis

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          aria-label="前の章"
          disabled={index <= 0}
          onClick={() => setBrowsingIndex(index - 1)}
        >
          <ChevronLeft className="size-5" />
        </Button>
        <div className="min-w-0 flex-1 text-center">
          <p className="truncate text-sm font-semibold">{section.title}</p>
          <p className="text-xs text-muted-foreground">
            {index + 1} / {sections.length}
            {isCurrent && <span className="ml-1 text-primary">・今読んでいる章</span>}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="次の章"
          disabled={index >= sections.length - 1}
          onClick={() => setBrowsingIndex(index + 1)}
        >
          <ChevronRight className="size-5" />
        </Button>
      </div>

      {!isCurrent && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => onNavigate(section.start)}>
            <CornerDownRight className="size-4" />
            この章へ移動
          </Button>
          {currentSectionIndex >= 0 && (
            <Button variant="ghost" size="sm" onClick={() => setBrowsingIndex(null)}>
              今の章に戻る
            </Button>
          )}
        </div>
      )}

      {!analysis ? (
        <p className="py-6 text-center text-sm text-muted-foreground">この章はまだ解析されていません</p>
      ) : (
        <>
          <section className="space-y-1.5">
            <h3 className="text-xs font-semibold text-muted-foreground">要約</h3>
            <p className="text-sm leading-relaxed wrap-break-word">{analysis.summary}</p>
          </section>

          <ChapterPurposePassages episodeId={bookAi.episodeId} sectionIndex={index} onNavigate={onNavigate} />

          {analysis.questions.length > 0 && (
            <section className="space-y-1.5">
              <h3 className="text-xs font-semibold text-muted-foreground">読む前に頭に置く問い</h3>
              <ul className="space-y-2">
                {analysis.questions.map((question, i) => {
                  const questionKey = `${index}-${i}`
                  return (
                    <QuestionItem
                      key={questionKey}
                      episodeId={bookAi.episodeId}
                      sectionIndex={index}
                      question={question}
                      isOpen={openQuestion === questionKey}
                      onOpen={() => setOpenQuestion(questionKey)}
                      onNavigate={onNavigate}
                    />
                  )
                })}
              </ul>
            </section>
          )}

          <PurposeQuestions
            episodeId={bookAi.episodeId}
            sectionIndex={index}
            openQuestion={openQuestion}
            onOpenQuestion={setOpenQuestion}
            onNavigate={onNavigate}
          />

          {/* 章を切り替えたら「すべて表示」を戻す */}
          <ChapterKeyPoints key={section.index} section={section} onNavigate={onNavigate} />

          {analysis.keywords.length > 0 && (
            <section className="space-y-1.5">
              <h3 className="text-xs font-semibold text-muted-foreground">キーワード</h3>
              <div className="flex flex-wrap gap-1.5">
                {analysis.keywords.map((keyword) => (
                  <Badge key={keyword} variant="secondary">
                    {keyword}
                  </Badge>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}
